import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { trpc } from "@/utils/trpc";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useWorkspaceSection, type WorkspaceSection } from "@/contexts/workspace-section-context";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { RouterAppContext } from "@/utils/custom-types";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";
import { useWorkspaceContext } from "@/contexts/workspace-context";

const CHANNEL_TYPES = [
	"chat",
	"doc",
	"board",
	// "self-dm",
	// "dm",
	// "sheet",
	// "meeting",
	// "storage",
	// "password",
	// "automation",
	// "project",
] as const;

type ChannelType = typeof CHANNEL_TYPES[number];

// const SECTION_TO_TYPES: Record<Exclude<WorkspaceSection, null>, readonly ChannelType[]> = {
//   projects: ["project"],
//   chats: ["message-group", "dm", "self-dm"],
//   docs: ["doc"],
//   boards: ["board"],
//   sheets: ["sheet"],
//   meets: ["meeting"],
//   storage: ["storage"],
//   passwords: ["password"],
//   automations: ["automation"],
//   more: CHANNEL_TYPES,
// };

const channelSchema = z.object({
	title: z
		.string()
		.min(2, "Title must be at least 2 characters")
		.max(50, "Title must be at most 50 characters"),
	description: z
		.string()
		.max(200, "Description must be at most 200 characters")
		.optional()
		.or(z.literal("")),
	type: z.enum(CHANNEL_TYPES, 'Please pick a type')
});

type ChannelFormValues = z.infer<typeof channelSchema>;

interface CreateChannelDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	channelVisibility: "public" | "private";
}

export function CreateChannelDialog({
	open,
	onOpenChange,
	channelVisibility
}: CreateChannelDialogProps) {
	// const {
	// 	isPending,
	// 	error,
	// } = useQuery(trpc.me.getMe.queryOptions());

	const { activeSection } = useWorkspaceContext();

	// if (error) return <div>Error {error.message}</div>;
	// if (isPending) return <div>Pending...</div>;

	const { currentWorkspace } = WorkspaceRoute.useLoaderData();

	const queryClient = useQueryClient();
	const createChannel = useMutation(
		trpc.channel.create.mutationOptions({
			onSuccess: () => {
				// Invalidate channels query to refetch
				queryClient.invalidateQueries({
					queryKey: trpc.channel.getByUserAndWorkspace.queryKey({
						workspaceId: Number(currentWorkspace.id),
					}),
				});
			},
		}),
	);

	const {
		register,
		handleSubmit,
		reset,
		resetField,
		formState: { errors, isSubmitting, submitCount },
		setError,
		setValue,
		watch,
	} = useForm<ChannelFormValues>({
		resolver: zodResolver(channelSchema),
		defaultValues: { title: "", description: "", type: CHANNEL_TYPES[0] },
	});

	// const allowedTypes = useMemo(() => {
	// 	if (activeSection === "home") return CHANNEL_TYPES;

	// 	return SECTION_TO_TYPES[activeSection] ?? [];
	// }, [activeSection])

	// useEffect(() => {
	// 	if (!open) return;

	// 	if (allowedTypes.length === 1) {
	// 		setValue("type", allowedTypes[0], { shouldValidate: true, shouldDirty: false });
	// 		return;
	// 	}

	// 	resetField("type", { defaultValue: undefined });
	// }, [open, allowedTypes, resetField, setValue])

	// Reset form when dialog closes
	useEffect(() => {
		if (!open) {
			reset();
		}
	}, [open, reset]);

	const onSubmit = async (data: ChannelFormValues) => {
		return new Promise<void>((resolve) => {
			createChannel.mutate(
				{
					workspaceId: Number(currentWorkspace.id),
					title: data.title.trim(),
					description: data.description?.trim() || undefined,
					type: data.type === "chat" ? "message-group" : data.type,
					visibility: channelVisibility,
				},
				{
					onSuccess: () => {
						onOpenChange(false);
						reset();
						resolve();
						toast.success("Channel created successfully");
					},
					onError: (err: any) => {
						setError("root", {
							message: err?.message || "Failed to create channel",
						});
						resolve();
					},
				},
			);
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Create Channel</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
					<div>
						<label
							htmlFor="channel-title"
							className="mb-1 block font-medium text-sm"
						>
							Title <span className="text-red-500">*</span>
						</label>
						<Input
							id="channel-title"
							{...register("title")}
							placeholder="Channel name"
							disabled={createChannel.isPending || isSubmitting}
							aria-invalid={!!errors.title}
						/>
						{errors.title && (
							<div className="mt-1 text-red-500 text-sm">
								{errors.title.message}
							</div>
						)}
					</div>
					<div>
						<label
							htmlFor="channel-description"
							className="mb-1 block font-medium text-sm"
						>
							Description
						</label>
						<Input
							id="channel-description"
							{...register("description")}
							placeholder="(Optional)"
							disabled={createChannel.isPending || isSubmitting}
							aria-invalid={!!errors.description}
						/>
						{errors.description && (
							<div className="mt-1 text-red-500 text-sm">
								{errors.description.message}
							</div>
						)}
					</div>
					<div>
						<label
							htmlFor="channel-type"
							className="mb-1 block font-medium text-sm"
						>
							Type <span className="text-red-500">*</span>
						</label>
						<Select
							disabled={createChannel.isPending || isSubmitting}
							value={watch("type") ?? undefined}
							onValueChange={(val) => setValue("type", val as any, { shouldValidate: true })}
						>
							<SelectTrigger id="channel-type" className="dark:bg-input/30">
								<SelectValue placeholder="Select channel type" />
							</SelectTrigger>
							<SelectContent>
								{CHANNEL_TYPES.map((t) => (
									<SelectItem key={t} value={t}>
										{t}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{errors.type && submitCount > 0 && (
							<div className="mt-1 text-red-500 text-sm">
								{errors.type.message}
							</div>
						)}
					</div>
					<input 
						className="hidden"
						{...register("type")} 
						aria-invalid={!!errors.type}
					/>
					{/* {errors.type && submitCount > 0 && (
								<div className="mt-1 text-red-500 text-sm">
									{errors.type.message}
								</div>
							)} */}
					{errors.root && (
						<div className="text-red-500 text-sm">{errors.root.message}</div>
					)}
					{createChannel.isError && !errors.root && (
						<div className="text-red-500 text-sm">
							{createChannel.error?.message || "Failed to create channel"}
						</div>
					)}
					<div className="flex justify-end gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={createChannel.isPending || isSubmitting}
							className="cursor-pointer"
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={createChannel.isPending || isSubmitting}
							className="cursor-pointer"
						>
							{createChannel.isPending || isSubmitting
								? "Creating..."
								: "Create"}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
}
